import { SxProps } from '@mui/material';
import { CSSProperties } from 'react';

import {
  badgeYellow,
  borderLight,
  successGreen,
  textMuted,
  textPrimary,
  white,
} from 'app/components/ThemeProvider/colors';
import { getTheme } from 'app/components/ThemeProvider/theme';

const theme = getTheme();

/* Sidebar container */
export const sidebarMenuBox: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  width: '341px',
  height: 'calc(100vh - 80px)',
  p: '6px 0 16px 16px',
  boxSizing: 'border-box',
  background: white,
  borderRight: `1px solid ${borderLight}`,
  overflow: 'auto',

  [theme.breakpoints.down('md')]: {
    width: '100%',
    height: 'auto',
    flex: 1,
    minHeight: 0,
    border: 'none',
    p: '16px 8px',
  },
};

/* Content (menu list) */
export const menuBox: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  paddingRight: '10px',
  paddingTop: '6px',
};

/* Menu Item 1.0 */
export const menuItem: CSSProperties = {
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px',
  height: '56px',
  padding: '16px',
  borderRadius: '10px',
  background: white,
  cursor: 'pointer',
};

export const activeMenuItem: CSSProperties = {
  background: badgeYellow,
};

/* Disabled row (unregistered/guest menu) — greyed out and not clickable */
export const disabledMenuItem: CSSProperties = {
  cursor: 'default',
  opacity: 0.4,
};

export const menuIcon: CSSProperties = {
  width: 24,
  height: 24,
  flex: 'none',
};

/* Hover background for clickable rows */
export const hoverBg = '#F6F7F7';

export const menuItemHover = {
  '&:hover': { background: hoverBg },
};

/* Submenu hover — background stays, only the label color darkens */
export const submenuItemHover = {
  '&:hover .MuiTypography-root': { color: textPrimary },
};

/* Accordion chevron — collapsed points down, expanded flips up (animated) */
export const chevronIcon: CSSProperties = {
  width: 24,
  height: 24,
  flex: 'none',
  transition: 'transform 0.25s ease',
};

export const chevronExpanded: CSSProperties = {
  transform: 'rotate(180deg)',
};

export const menuLabel: SxProps = {
  flex: 1,
  fontFamily: 'Yelo',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  color: textPrimary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

/* Count badge — green pill on every row, active or not */
export const countBadge = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flex: 'none',
  p: '4px 8px',
  minWidth: '24px',
  height: '24px',
  borderRadius: '24px',
  background: successGreen,
};

export const countBadgeText = {
  fontFamily: 'Yelo',
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: '16px',
  color: white,
};

/* Subtitle badge ("yeni") — green pill on every row, active or not */
export const subtitleBadge = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flex: 'none',
  p: '4px 8px',
  height: '24px',
  borderRadius: '24px',
  background: successGreen,
};

export const subtitleBadgeText = {
  fontFamily: 'Yelo',
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: '16px',
  color: white,
  textTransform: 'capitalize',
};

/* Submenu list — indented 32px under the expanded parent */
export const submenuList: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  pl: '32px',
};

export const submenuLabel: CSSProperties = {
  flex: 1,
  fontFamily: 'Yelo',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  color: textMuted,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

/* Emphasized submenu item (has icon or is the current route) */
export const submenuLabelActive: CSSProperties = {
  fontWeight: 600,
  color: textPrimary,
};

/* Bottom section — divider + logout */
export const bottomSection: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  flex: 'none',
  paddingRight: '16px',
};

export const divider: SxProps = {
  borderColor: borderLight,
};
